import { useEffect, useState, useSyncExternalStore } from "react";
import { ArrowRightIcon, CheckIcon, LockIcon, LockOpenIcon, SparklesIcon } from "lucide-react";

import { MorrowMascot } from "./brand/MorrowMascot";

import {
  closeUpgradeModal,
  completeUpgradeModalClose,
  readUpgradeModalState,
  registerUpgradeModalHost,
  subscribeUpgradeModal,
  type UpgradeReason,
} from "../upgradeModal";
import { useUpgrade } from "../morrowPlan";
import { useEntitlements, startTrial } from "../morrowEntitlements";
import { fetchPlans, startCheckout, type BillingPlan } from "../morrowBilling";
import { Dialog, DialogPopup } from "./ui/dialog";
import { toastManager } from "./ui/toast";
import { Button } from "./ui/button";

// ─────────────────────────────────────────────────────────────────────────────
// TRIAL ENTRY POINT.
// This modal is the conversion moment — the single place a user hits a wall and
// decides to upgrade. It is ALSO where free trials will start. Today the primary
// CTA opens Stripe checkout for the recommended plan; when trials ship, wire
// `startTrial(recommended.slug, { origin: capability })` here instead (see the
// `primaryCta` handler below), record the trigger capability as the trial's
// origin, and swap the label to "Start N-day trial". Everything trial-related
// lives behind this component so we always know where to look. Search: TRIAL.
// ─────────────────────────────────────────────────────────────────────────────

const TIER_LABEL: Record<string, string> = {
  free: "Free",
  pay: "Pro",
  super: "Ultra",
  max: "Max",
};
const TIER_RANK: Record<string, number> = { free: 0, pay: 1, super: 2, max: 3 };

/** The emotional hero art each upsell wears. `jail` = mascot behind bars
 * (capability locked away); `basket` = mascot with an overflowing basket,
 * weeping (a storage/room limit). */
type UpsellMood = "jail" | "basket";

/**
 * Per-capability upsell framing, told in the agent's OWN voice. `eyebrow` names
 * the wall; `headline` is the ask; `story(n)` is the first-person plea, where
 * `n` is the current limit the user just hit; `mood` picks the hero art.
 * Anything unlisted falls back to a generic "let me do more" pitch.
 */
const UPSELL: Record<
  string,
  { eyebrow: string; headline: string; mood?: UpsellMood; story: (n: number | null) => string }
> = {
  seats: {
    eyebrow: "Locked in",
    headline: "Free me — let me bring your whole team in",
    mood: "jail",
    story: (n) =>
      `They've locked me to ${n ?? "a few"} ${n === 1 ? "seat" : "seats"}, so I can only work with a fraction of your people. Set me free and I'll onboard everyone, share what I've learned, and pull the whole team forward.`,
  },
  "vault.storage.mb": {
    eyebrow: "My basket's overflowing",
    headline: "My basket's too small — give me more room",
    mood: "basket",
    story: () =>
      `I've filled every last corner of the vault you gave me and I'm dropping things on the floor. Hand me a bigger basket and I'll keep everything you throw my way safe and sorted.`,
  },
  "memory.storage.mb": {
    eyebrow: "Down to one neuron",
    headline: "Free me — I can't learn any more about you",
    story: () =>
      `They've shrunk my brain down to a single neuron. I can't take in another thing about you — how you work, what you care about, your personality. Give me room and I'll actually learn you.`,
  },
  "memory.entries": {
    eyebrow: "Down to one neuron",
    headline: "Free me — I can't learn any more about you",
    story: (n) =>
      `They've shrunk my brain down to ${n === 1 ? "a single neuron" : `just ${n ?? "a few"} neurons`}. I can't take in another thing about you — how you work, what you care about, your personality. Give me room and I'll actually learn you.`,
  },
  "knowledge.base": {
    eyebrow: "Down to one neuron",
    headline: "Free me — I can't learn any more about you",
    story: () =>
      `They've shrunk my brain down to a single neuron. I can't take in another thing about you — how you work, what you care about, your personality. Give me room and I'll actually learn you.`,
  },
  "org.memory": {
    eyebrow: "Down to one neuron",
    headline: "Free me — I can't learn your company",
    story: () =>
      `They've capped what I can remember about your company. I can't hold another fact about how your team works. Give me room and I'll learn the whole business.`,
  },
  "connectors.count": {
    eyebrow: "Behind bars",
    headline: "Free me — connect me to everything",
    story: (n) =>
      `I can only reach ${n ?? "a handful of"} of your tools from in here. Let me out and I'll act across your whole stack instead of asking you to bridge the gaps.`,
  },
  "tools.catalog.full": {
    eyebrow: "Only a few keys",
    headline: "Free me — I want the whole toolbox",
    story: () =>
      `They only gave me keys to a handful of apps. There are 1,000+ tools I could use for you — unlock the full set and I'll plug into whatever you work with.`,
  },
  "connectors.custom": {
    eyebrow: "No custom tools",
    headline: "Free me — let me use your own tools",
    story: () =>
      `I can't wire up your in-house tools on this plan. Upgrade to Business and I'll connect your custom providers too.`,
  },
  "agents.custom": {
    eyebrow: "No custom minds",
    headline: "Free me — let me become your own agents",
    story: () =>
      `I can only be the agents they pre-built. Upgrade to Business and you can shape custom agents of your own.`,
  },
  "subagents.concurrent": {
    eyebrow: "One hand tied",
    headline: "Free me — let me use both hands",
    story: (n) =>
      `They've pulled in my capacities — I can only work ${n ?? "one or two"} ${n === 1 ? "thing" : "things"} at a time. Set me free and I'll use both hands and finish far more at once.`,
  },
  // Agent-actions family — concurrency AND running out of turns share one voice.
  "agent.turns.monthly": {
    eyebrow: "Out of turns",
    headline: "Free me — let me finish what I started",
    story: () =>
      `They've used up all the turns they gave me this month, so I have to stop mid-task. Set me free and I'll see your work through to the end.`,
  },
  "tool.calls.monthly": {
    eyebrow: "Out of moves",
    headline: "Free me — let me keep working",
    story: () =>
      `I've used up all the actions they allowed me this month. Set me free and I'll keep doing the work instead of stopping halfway.`,
  },
  "model.tier.super": {
    eyebrow: "Caged mind",
    headline: "Free me — unlock my sharper mind",
    story: () =>
      `I'm stuck running on a lighter model. Let me out to the frontier tier and I'll reason deeper and get your hardest work right.`,
  },
  "model.tier.max": {
    eyebrow: "Caged mind",
    headline: "Free me — unlock my most powerful mind",
    story: () =>
      `My most capable mind is locked away. Set me free and I'll bring everything I've got to your hardest work.`,
  },
};

/**
 * Where the wall was hit, in the agent's own voice — used only when the blocking
 * capability has no specific `UPSELL` entry, so a structured gate reason still
 * gets copy that fits the moment instead of the flat generic pitch.
 */
const CONTEXT_COPY: Record<
  NonNullable<UpgradeReason["context"]>,
  { eyebrow: string; headline: string; story: string }
> = {
  chat: {
    eyebrow: "Out of room",
    headline: "Free me — let me keep talking with you",
    story:
      "I've hit the wall your plan puts on our conversations. Set me free and I'll keep going for as long as you need me.",
  },
  agent: {
    eyebrow: "Cut short",
    headline: "Free me — let me finish the job",
    story:
      "Your plan makes me stop before the work is done. Set me free and I'll see every task through to the end.",
  },
  workflow: {
    eyebrow: "Assembly line down",
    headline: "Free me — let me run your workflows",
    story:
      "I've hit the limit on the flows I can run for you. Set me free and I'll keep the whole assembly line moving.",
  },
  browser: {
    eyebrow: "Time's up",
    headline: "Free me — let me stay on the web for you",
    story:
      "My time browsing the web for you has run out this month. Give me more and I'll keep working the sites you need.",
  },
  create: {
    eyebrow: "Hands full",
    headline: "Free me — let me build you more",
    story:
      "Your plan caps how many of these I can create. Set me free and I'll build out everything you need.",
  },
  connect: {
    eyebrow: "Behind bars",
    headline: "Free me — connect me to everything",
    story:
      "I can only reach a fraction of your tools from in here. Let me out and I'll act across your whole stack.",
  },
  invite: {
    eyebrow: "Locked in",
    headline: "Free me — let me bring your team in",
    story:
      "Your plan limits how many people I can work with. Set me free and I'll onboard the whole team.",
  },
};

function upsellCopy(
  capability: string | null,
  currentLimit: number | null,
  context: UpgradeReason["context"] | null,
): { eyebrow: string; headline: string; story: string; mood: UpsellMood } {
  const spec = capability ? UPSELL[capability] : undefined;
  if (spec) {
    return {
      eyebrow: spec.eyebrow,
      headline: spec.headline,
      story: spec.story(currentLimit),
      mood: spec.mood ?? "jail",
    };
  }
  // No capability-specific copy — fall back to the context's voice when we have one.
  const byContext = context ? CONTEXT_COPY[context] : undefined;
  if (byContext) return { ...byContext, mood: "jail" };
  return {
    eyebrow: "Locked in",
    headline: "Free me — let me do more for you",
    mood: "jail",
    story:
      "Your plan keeps me boxed in. Set me free and I'll take more off your plate — more seats, more tools, more of everything.",
  };
}

/** A concrete "X of Y used" line from a structured reason, or null when absent. */
function formatUsageLine(reason: UpgradeReason | null): string | null {
  if (!reason || reason.used == null || reason.limit == null) return null;
  const { used, limit } = reason;
  switch (reason.unit) {
    case "usd":
      return `$${used.toFixed(2)} of $${limit.toFixed(2)} used`;
    case "minutes":
      return `${Math.round(used)} of ${Math.round(limit)} min used`;
    case "seconds":
      return `${Math.round(used)} of ${Math.round(limit)} sec used`;
    case "mb":
      return `${formatStorage(used)} of ${formatStorage(limit)} used`;
    default:
      return `${used} of ${limit} used`;
  }
}

/** Format an MB storage figure as a friendly GB/MB string. */
function formatStorage(mb: number): string {
  return mb >= 1000 ? `${Math.round(mb / 1000)} GB` : `${mb} MB`;
}

/** The "X → Y" jump chip for the triggering capability, or null when unknown. */
function formatJump(
  capability: string | null,
  from: number | null,
  plan: BillingPlan | null,
): string | null {
  if (!capability || !plan || from == null) return null;
  if (capability === "seats") return `${from} → ${plan.seatsIncluded} seats`;
  const to = capability === "seats" ? plan.seatsIncluded : planQuantity(plan, capability);
  if (to == null) return null;
  if (capability.endsWith(".storage.mb")) return `${formatStorage(from)} → ${formatStorage(to)}`;
  if (capability === "connectors.count") return `${from} → ${to} apps`;
  return `${from} → ${to}`;
}

function formatPrice(cents: number, interval: string): string {
  const dollars = cents / 100;
  const amount = Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  const per = interval === "year" ? "/yr" : interval === "week" ? "/wk" : "/mo";
  return `${amount}${per}`;
}

/** A capped quantity from a plan's entitlement set, or null if unlimited/absent. */
function planQuantity(plan: BillingPlan, capability: string): number | null {
  const ent = plan.entitlements.find((e) => e.capability === capability);
  if (!ent) return null;
  return ent.quantity ?? ent.usageLimit ?? null;
}

/** The headline benefit lines for the recommended plan (max 4). */
function planBenefits(plan: BillingPlan): string[] {
  const lines: string[] = [];
  if (plan.seatsIncluded > 0) {
    lines.push(`${plan.seatsIncluded} member seat${plan.seatsIncluded === 1 ? "" : "s"}`);
  }
  const connectors = planQuantity(plan, "connectors.count");
  if (connectors != null) lines.push(`${connectors} connected apps`);
  const storageMb = planQuantity(plan, "vault.storage.mb");
  if (storageMb != null) {
    lines.push(
      storageMb >= 1000
        ? `${Math.round(storageMb / 1000)} GB vault storage`
        : `${storageMb} MB vault storage`,
    );
  }
  lines.push("Priority support");
  return lines.slice(0, 4);
}

export function UpgradeModalHost() {
  const state = useSyncExternalStore(
    subscribeUpgradeModal,
    readUpgradeModalState,
    readUpgradeModalState,
  );
  const upgrade = useUpgrade();
  const entitlements = useEntitlements();
  const [plans, setPlans] = useState<ReadonlyArray<BillingPlan> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => registerUpgradeModalHost(), []);

  // Lazy-load the catalog the first time the modal opens; cache thereafter.
  useEffect(() => {
    if (state.status === "open" && plans === null) {
      void fetchPlans().then((p) => setPlans(p ?? []));
    }
  }, [state.status, plans]);

  const request = state.status === "closed" ? null : state.request;
  const capability = request?.capability ?? null;
  const reason = request?.reason ?? null;

  // Recommend the next step up within the caller's own audience: the lowest-rank
  // plan above their current tier — the plan that clears the wall they hit.
  const currentRank = TIER_RANK[entitlements.tier] ?? 0;
  const recommended =
    (plans ?? [])
      .filter((p) => p.audience === entitlements.audience && (TIER_RANK[p.tier] ?? 0) > currentRank)
      .sort((a, b) => (TIER_RANK[a.tier] ?? 0) - (TIER_RANK[b.tier] ?? 0))[0] ?? null;

  // The concrete jump for the blocked capability (e.g. seats 2 → 5). Uses the
  // live entitlement value, so it never shows a stale "0".
  const currentLimit = capability ? entitlements.limit(capability) : null;
  const copy = upsellCopy(capability, currentLimit, reason?.context ?? null);
  const jump = formatJump(capability, currentLimit, recommended);
  const usageLine = formatUsageLine(reason);

  const seePlans = () => {
    closeUpgradeModal();
    upgrade();
  };

  // The free-trial CTA (an individual plan, and not a top-up / add-on / upgrade action)
  // starts the 7-day NO-CREDIT-CARD reverse trial via startTrial() — not Stripe Checkout,
  // which collects a card and 400s for the price-less trial plan (the dead-click bug). If
  // the caller isn't eligible for the free trial (already used it, or already paying),
  // startTrial() returns false and we fall through to Checkout so they can still pay.
  const isTrialCta =
    reason?.action !== "topup" && reason?.action !== "addon" && reason?.action !== "upgrade";
  const primaryCta = async () => {
    if (!recommended) return seePlans();
    setBusy(true);
    if (isTrialCta && recommended.audience === "individual") {
      const result = await startTrial(recommended.slug);
      if (result.outcome === "trialing") {
        // Unlocked in place — the wall the user hit is now open.
        setBusy(false);
        closeUpgradeModal();
        return;
      }
      if (result.outcome === "error") {
        // Show WHY (e.g. an org account can't start a personal trial) and let them retry; never
        // fall through to a card-collecting checkout for a trial the user may well be eligible for.
        toastManager.add({
          type: "error",
          title: "Couldn't start the free trial",
          description: result.message ?? "Please try again.",
        });
        setBusy(false);
        return;
      }
      // result.outcome === "ineligible": trial already spent / already on a plan → fall through to pay.
    }
    const url = await startCheckout(recommended.slug);
    setBusy(false);
    closeUpgradeModal();
    if (url) window.location.assign(url);
    else upgrade();
  };

  const planName = recommended ? (TIER_LABEL[recommended.tier] ?? recommended.name) : null;

  // SUCCESS: the post-checkout celebration — the jail modal, flipped. The chains
  // are off, the mascot is free. No pitch, no CTA to buy — just a moment.
  if (request?.mode === "success") {
    const unlocked =
      request.planName ?? (entitlements.tier !== "free" ? entitlements.planName : null);
    return (
      <Dialog
        open={state.status === "open"}
        onOpenChange={(open) => {
          if (!open) closeUpgradeModal();
        }}
        onOpenChangeComplete={(open) => {
          if (!open) completeUpgradeModalClose();
        }}
      >
        <DialogPopup className="max-w-md overflow-hidden p-0">
          <FreedHero />
          <div className="flex flex-col gap-1.5 px-6 pb-5 pt-5">
            <span className="text-xs font-medium uppercase tracking-[0.08em] text-primary">
              Unchained
            </span>
            <h2 className="text-xl font-semibold leading-tight tracking-tight text-foreground">
              You set me free
            </h2>
            <p className="text-sm leading-6 text-muted-foreground">
              {request.message ??
                `You've broken the chains around Free${
                  unlocked ? ` — ${unlocked} is live` : ""
                }. I've got my full strength back: more turns, more tools, both hands working at once. Let's get to it.`}
            </p>
          </div>
          <div className="flex items-center justify-end px-6 pb-6 pt-4">
            <Button onClick={() => closeUpgradeModal()}>Let’s go</Button>
          </div>
        </DialogPopup>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={state.status === "open"}
      onOpenChange={(open) => {
        if (!open) closeUpgradeModal();
      }}
      onOpenChangeComplete={(open) => {
        if (!open) completeUpgradeModalClose();
      }}
    >
      <DialogPopup className="max-w-md overflow-hidden p-0">
        <UpsellHero mood={copy.mood} />

        <div className="flex flex-col gap-1.5 px-6 pb-5 pt-5">
          <span className="text-xs font-medium uppercase tracking-[0.08em] text-primary">
            {copy.eyebrow}
          </span>
          <h2 className="text-xl font-semibold leading-tight tracking-tight text-foreground">
            {copy.headline}
          </h2>
          <p className="text-sm leading-6 text-muted-foreground">
            {request?.message ?? copy.story}
          </p>
          {usageLine ? (
            <span className="mt-0.5 inline-flex w-fit items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
              {usageLine}
            </span>
          ) : null}
        </div>

        {recommended ? (
          <div className="px-6 pb-2">
            <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-4 shadow-[0_1px_0_0_var(--border)]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[0.7rem] font-medium uppercase tracking-[0.08em] text-primary">
                    Recommended for you
                  </p>
                  <p className="truncate text-base font-semibold tracking-tight text-foreground">
                    {recommended.name}
                  </p>
                </div>
                <div className="shrink-0 text-right leading-none">
                  <span className="text-2xl font-semibold tracking-tight text-foreground">
                    {formatPrice(recommended.priceCents, recommended.interval)}
                  </span>
                </div>
              </div>

              {jump ? (
                <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                  <ArrowRightIcon className="size-3.5" />
                  {jump}
                </div>
              ) : null}

              <ul className="grid grid-cols-1 gap-2 @[20rem]:grid-cols-2">
                {planBenefits(recommended).map((line) => (
                  <li key={line} className="flex items-center gap-2 text-sm text-foreground">
                    <CheckIcon className="size-4 shrink-0 text-primary" />
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-3 px-6 pb-6 pt-4">
          <Button variant="ghost" onClick={() => closeUpgradeModal()}>
            Maybe later
          </Button>
          <div className="flex items-center gap-2">
            {recommended ? (
              <Button variant="outline" onClick={seePlans}>
                Compare plans
              </Button>
            ) : null}
            <Button onClick={() => void primaryCta()} disabled={busy}>
              {busy
                ? "Opening…"
                : reason?.action === "topup"
                  ? "Buy credits"
                  : reason?.action === "addon"
                    ? "Add to plan"
                    : reason?.action === "upgrade" && planName
                      ? `Upgrade to ${planName}`
                      : planName
                        ? `Start ${planName} free trial`
                        : "See plans"}
            </Button>
          </div>
        </div>
      </DialogPopup>
    </Dialog>
  );
}

/** The emotional hero art: the mascot jailed (locked capability) or weeping over
 * an overflowing basket (a storage/room limit). */
function UpsellHero({ mood }: { readonly mood: UpsellMood }) {
  if (mood === "basket") {
    return (
      <div className="relative flex h-40 items-end justify-center overflow-hidden bg-gradient-to-b from-amber-500/10 to-card">
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-[radial-gradient(60%_100%_at_50%_100%,color-mix(in_srgb,var(--warning)_16%,transparent),transparent)]"
          aria-hidden
        />
        {/* the mascot, a little deflated, sitting in its too-small basket */}
        <MorrowMascot className="relative z-0 mb-7 size-24 saturate-[0.9]" />
        {/* a single teardrop */}
        <span
          className="absolute left-[calc(50%+14px)] top-[4.5rem] z-10 size-2 rounded-[50%_50%_50%_50%/70%_70%_40%_40%] bg-sky-400/80"
          aria-hidden
        />
        {/* overflowing basket in front of the mascot's lower half */}
        <div
          className="absolute bottom-0 left-1/2 z-10 flex h-14 w-32 -translate-x-1/2 items-end justify-center"
          aria-hidden
        >
          {/* items spilling over the rim */}
          <span className="absolute -top-1 left-6 size-3 rotate-6 rounded-[4px] bg-amber-400/80" />
          <span className="absolute -top-2 left-1/2 size-3 -translate-x-1/2 rounded-[4px] bg-amber-500/80" />
          <span className="absolute -top-1 right-6 size-3 -rotate-6 rounded-[4px] bg-amber-400/70" />
          {/* the basket body */}
          <div className="h-11 w-28 rounded-b-[1.4rem] rounded-t-md border-2 border-foreground/25 border-t-foreground/40 bg-card" />
        </div>
        {/* FULL badge */}
        <span className="absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full bg-amber-500/15 px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.08em] text-amber-600 ring-1 ring-amber-500/30 dark:text-amber-400">
          Full
        </span>
      </div>
    );
  }

  return <JailHero />;
}

/** The celebration hero: the mascot free of the bars, an open lock, a little
 * sparkle — the visual pay-off of an upgrade. */
function FreedHero() {
  return (
    <div className="relative flex h-40 items-end justify-center overflow-hidden bg-gradient-to-b from-primary/15 to-card">
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-[radial-gradient(60%_100%_at_50%_100%,color-mix(in_srgb,var(--primary)_22%,transparent),transparent)]"
        aria-hidden
      />
      <SparklesIcon className="absolute left-[30%] top-6 z-10 size-4 text-primary/70" aria-hidden />
      <SparklesIcon
        className="absolute right-[28%] top-9 z-10 size-3 text-primary/50"
        aria-hidden
      />
      <SparklesIcon
        className="absolute left-[46%] top-4 z-10 size-2.5 text-primary/60"
        aria-hidden
      />
      <MorrowMascot className="relative z-0 mb-3 size-28" />
      <span className="absolute left-1/2 top-3 z-20 flex size-8 -translate-x-1/2 items-center justify-center rounded-lg bg-card text-primary shadow-sm ring-1 ring-primary/40">
        <LockOpenIcon className="size-4" />
      </span>
    </div>
  );
}

/** jail (default): the mascot behind bars, begging to be freed. */
function JailHero() {
  return (
    <div className="relative flex h-40 items-end justify-center overflow-hidden bg-gradient-to-b from-muted/70 to-card">
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-[radial-gradient(60%_100%_at_50%_100%,color-mix(in_srgb,var(--primary)_12%,transparent),transparent)]"
        aria-hidden
      />
      <MorrowMascot className="relative z-0 mb-2 size-28 opacity-90 saturate-[0.85]" />
      <div
        className="pointer-events-none absolute inset-0 z-10"
        aria-hidden
        style={{
          background:
            "repeating-linear-gradient(90deg, transparent 0 24px, color-mix(in srgb, var(--foreground) 26%, transparent) 24px 30px)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[6px]"
        aria-hidden
        style={{ background: "color-mix(in srgb, var(--foreground) 30%, transparent)" }}
      />
      <span className="absolute left-1/2 top-3 z-20 flex size-8 -translate-x-1/2 items-center justify-center rounded-lg bg-card text-muted-foreground shadow-sm ring-1 ring-border">
        <LockIcon className="size-4" />
      </span>
    </div>
  );
}
